import { sendTrackingHubEvent } from './tracking-hub.js';

const POSTHOG_TOKEN = 'phc_xFMsgTXNjgaQY4WibAb4EMf2965NNuSJ6X2QuZFcx5sN';
const POSTHOG_HOST = 'https://us.i.posthog.com';

const SENSITIVE_ROUTES = [
  /^\/novo-laudo(?:\/|$)/,
  /^\/editar-laudo(?:\/|$)/,
  /^\/laudo\/[^/]+/,
  /^\/report\/[^/]+/,
];

const FORBIDDEN_PROPERTY_KEYS = new Set([
  'patient', 'patient_name', 'patient_email', 'patient_phone', 'cpf', 'document',
  'clinical_text', 'report_text', 'description', 'diagnosis', 'conclusion', 'notes',
  'email', 'name', 'full_name', 'phone', 'address', 'content', 'raw_content',
]);

const ACQUISITION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'fbclid', 'acq_surface', 'acq_landing_path', 'acq_template_id',
];

const SEO_ANALYTICS_KEYS = new Set([
  'seo_page_type', 'seo_topic', 'seo_modality', 'seo_primary_intent', 'seo_path',
  'cta_action', 'template_id',
]);

let initialized = false;

const bootstrapPostHog = () => {
  if (typeof window === 'undefined') return null;
  if (window.posthog?.__loaded || window.posthog?.__SV) return window.posthog;

  const e = window.posthog || [];
  window.posthog = e;
  e._i = [];
  e.init = function init(token, config, name) {
    const addStub = (target, methodName) => {
      const parts = methodName.split('.');
      let owner = target;
      let method = methodName;
      if (parts.length === 2) {
        owner = target[parts[0]];
        method = parts[1];
      }
      owner[method] = function queuedMethod(...args) { owner.push([method, ...args]); };
    };

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.src = `${config.api_host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/1/array.js`;
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
    else document.head.appendChild(script);

    let target = e;
    const instanceName = name || 'posthog';
    if (name) target = e[name] = [];
    target.people = target.people || [];
    target.toString = () => `${instanceName} (stub)`;
    target.people.toString = () => `${instanceName}.people (stub)`;

    'init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' ').forEach((method) => addStub(target, method));
    e._i.push([token, config, name]);
  };
  e.__SV = 1;
  return e;
};

export const normalizeAnalyticsPath = (pathname = '/') => {
  const path = String(pathname || '/').split('?')[0].split('#')[0];
  if (/^\/editar-laudo\/[^/]+/.test(path)) return '/editar-laudo/:id';
  if (/^\/laudo\/[^/]+\/certificado/.test(path)) return '/laudo/:id/certificado';
  if (/^\/laudo\/[^/]+/.test(path)) return '/laudo/:id';
  if (/^\/report\/[^/]+\/certificate/.test(path)) return '/report/:id/certificate';
  if (/^\/report\/[^/]+/.test(path)) return '/report/:id';
  return path || '/';
};

export const isSensitiveAnalyticsRoute = (pathname = '/') =>
  SENSITIVE_ROUTES.some((pattern) => pattern.test(String(pathname || '/')));

const sanitizeProperties = (input = {}) => Object.fromEntries(
  Object.entries(input || {}).filter(([key, value]) => {
    if (FORBIDDEN_PROPERTY_KEYS.has(String(key).toLowerCase())) return false;
    return ['string', 'number', 'boolean'].includes(typeof value) || value === null;
  }),
);

export const buildSeoAnalyticsProperties = (input = {}) => Object.fromEntries(
  Object.entries(input || {})
    .filter(([key, value]) => SEO_ANALYTICS_KEYS.has(key) && ['string', 'number', 'boolean'].includes(typeof value))
    .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 160) : value]),
);

export const buildAnalyticsEventProperties = (properties = {}) => {
  const browserContext = typeof window === 'undefined' ? {} : {
    app_host: window.location?.hostname || null,
    site_mode: window.location?.hostname?.startsWith('app.') ? 'app' : 'public',
    route: normalizeAnalyticsPath(window.location?.pathname || '/'),
  };

  return sanitizeProperties({
    product: 'laudando',
    ...browserContext,
    ...properties,
  });
};

const reportEventProperties = (report = {}) => ({
  template_id: report.template_id || null,
  used_template: Boolean(report.template_id),
});

const reportIsMeaningful = (report = {}) => report.has_meaningful_content === true;

export const buildReportLifecycleEvents = (previousReports = [], currentReports = []) => {
  const previous = new Map((previousReports || []).map((report) => [report.id, report]));
  let activationAlreadyPresent = (previousReports || []).some(reportIsMeaningful);
  const events = [];

  for (const report of currentReports || []) {
    const prior = previous.get(report.id);
    const properties = reportEventProperties(report);
    const becameMeaningful = reportIsMeaningful(report) && !reportIsMeaningful(prior);

    if (becameMeaningful && report.status === 'draft') {
      events.push({ event: 'report_draft_saved', properties });
    }

    if (becameMeaningful && !activationAlreadyPresent) {
      events.push({ event: 'first_report_created', properties });
      activationAlreadyPresent = true;
    }

    if (report.status === 'signed' && prior?.status !== 'signed') {
      events.push({ event: 'report_emitted', properties });
    }
  }

  return events;
};

const inferTemplateIdFromRedirect = (params) => {
  const direct = params.get('templateId');
  if (direct) return direct;
  const redirect = params.get('redirect');
  if (!redirect) return null;
  try {
    const nested = new URL(redirect, window.location.origin);
    return nested.searchParams.get('templateId');
  } catch {
    return null;
  }
};

const inferAcquisitionSurfaceFromPath = (pathname = '/') => {
  const path = String(pathname || '/');
  if (path === '/modelos-laudo') return 'public_template_catalog';
  if (path.startsWith('/modelos-laudo/')) return 'public_template';
  if (path === '/referencias-laudos' || path.startsWith('/referencias-laudos/')) return 'seo_reference';
  if (['/sistema-de-laudos-medicos', '/software-para-laudos-medicos', '/programa-para-laudos-medicos'].includes(path)) return 'seo_commercial';
  return null;
};

export const initAnalytics = async () => {
  if (initialized || typeof window === 'undefined') return window?.posthog || null;
  const posthog = bootstrapPostHog();
  if (!posthog?.init) return null;

  posthog.init(POSTHOG_TOKEN, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    person_profiles: 'identified_only',
    cross_subdomain_cookie: true,
    session_recording: { maskAllInputs: true },
  });
  initialized = true;
  captureAcquisition();
  return posthog;
};

export const captureAcquisition = () => {
  if (typeof window === 'undefined' || !window.posthog?.register_once) return;
  const params = new URLSearchParams(window.location.search);
  const props = {};
  for (const key of ACQUISITION_KEYS) {
    const value = params.get(key);
    if (value) props[`initial_${key}`] = value.slice(0, 300);
  }

  if (!props.initial_acq_template_id) {
    const inferredTemplateId = inferTemplateIdFromRedirect(params);
    if (inferredTemplateId) props.initial_acq_template_id = inferredTemplateId.slice(0, 300);
  }

  if (!props.initial_acq_landing_path && document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname === 'laudando.online' || referrer.hostname === 'www.laudando.online') {
        props.initial_acq_landing_path = referrer.pathname.slice(0, 300);
        if (!props.initial_acq_surface) props.initial_acq_surface = inferAcquisitionSurfaceFromPath(referrer.pathname) || undefined;
      }
    } catch { /* noop */ }
  }

  if (document.referrer) {
    try { props.initial_referrer_host = new URL(document.referrer).hostname; } catch { /* noop */ }
  }
  if (Object.keys(props).length) window.posthog.register_once(props);
};

export const track = (event, properties = {}) => {
  if (typeof window !== 'undefined' && !isSensitiveAnalyticsRoute(window.location?.pathname)) sendTrackingHubEvent(event, properties);
  if (!initialized || !window.posthog?.capture) return;
  window.posthog.capture(event, buildAnalyticsEventProperties(properties));
};

export const trackSeoPageView = (properties = {}) => track('seo_page_viewed', buildSeoAnalyticsProperties(properties));
export const trackSeoCta = (properties = {}) => track('seo_cta_clicked', buildSeoAnalyticsProperties(properties));

export const trackPageview = (pathname) => {
  if (!initialized || !window.posthog?.capture) return;
  const route = normalizeAnalyticsPath(pathname);
  window.posthog.capture('$pageview', {
    $current_url: `${window.location.origin}${route}`,
    route,
    product: 'laudando',
    app_host: window.location.hostname,
    site_mode: window.location.hostname.startsWith('app.') ? 'app' : 'public',
  });
};

export const identifyAnalyticsUser = (user, billingState = null) => {
  if (!initialized || !window.posthog || !user?.id) return;
  if (user.is_internal) {
    window.posthog.opt_out_capturing?.();
    return;
  }
  window.posthog.clear_opt_in_out_capturing?.();
  window.posthog.identify(user.id, {
    plan_tier: user.plan_tier || 'rotina',
    role: user.role || 'user',
    billing_source: billingState?.source || 'unknown',
    access_active: billingState?.active === true,
    trial_active: billingState?.source === 'trial',
    trial_ends_at: user.trial_ends_at || billingState?.trial_ends_at || null,
    is_internal: false,
  });
};

export const resetAnalytics = () => {
  if (!initialized || !window.posthog?.reset) return;
  window.posthog.reset(true);
};

export const updateSessionRecordingForRoute = (pathname) => {
  if (!initialized || !window.posthog) return;
  const sensitive = isSensitiveAnalyticsRoute(pathname);
  window.posthog.set_config?.({ autocapture: !sensitive });
  if (sensitive) window.posthog.stopSessionRecording?.();
  else window.posthog.startSessionRecording?.();
};

export const trackError = (context, error, extra = {}) => {
  const code = String(error?.code || error?.name || 'unknown_error').slice(0, 120);
  track('app_error', { context, error_code: code, ...extra });
  if (initialized && window.posthog?.captureException && error instanceof Error) {
    window.posthog.captureException(error, buildAnalyticsEventProperties({ context, ...extra }));
  }
};